import { useState, useCallback } from 'react';
import styles from './GoogleSearchConsoleAuth.module.css';
import getCMResponse from '../../../utils/api_manager';
import GoogleSearchConsoleIcon from '../../../assets/icons/google-search-console.svg';
import GoogleDataAccess from '../GoogleDataAccess/GoogleDataAccess';
import SiteUrlInputDialog from './SiteUrlInputDialog/SiteUrlInputDialog';

/**
 * GoogleSearchConsoleAuth - Handles Google Search Console authentication and site verification
 * @param {array} requiredScopes - Required OAuth scopes for Google Search Console
 * @param {array} grantedScopes - Currently granted OAuth scopes
 * @param {function} onGrantedScopesUpdate - Callback to refresh granted scopes
 * @param {object} project - Project object with id and other details
 * @param {string} providerProjectId - Site URL if already connected
 */
const GoogleSearchConsoleAuth = ({
    requiredScopes = [],
    grantedScopes = [],
    onGrantedScopesUpdate,
    project,
    providerProjectId,
    onLoginReady,
    // Grant-access loader centralization (requirement 3): mirrors this
    // component's own pre-existing `isVerifying` local state (which still
    // drives its in-dialog "Verifying site access..." text unchanged) so the
    // centralized per-tile overlay GrantOnboardingAccess renders behaves
    // identically to the other four connectors during the save call.
    // Optional -- only GrantOnboardingAccess supplies it today.
    onLoadingChange,
}) => {
    const [showUrlDialog, setShowUrlDialog] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState(null);

    // Check if all scopes are granted and site is verified
    const missingScopes = requiredScopes.filter((scope) => !grantedScopes.includes(scope));
    const allScopesGranted = missingScopes.length === 0;
    const isCompleted = allScopesGranted && providerProjectId != null;

    const handleAuthCallback = useCallback(() => {
        console.log('Google Search Console authentication successful');
        setError(null);
        setShowUrlDialog(true);
    }, []);

    const handleUrlSubmit = useCallback((siteUrl) => {
        setIsVerifying(true);
        setError(null);
        onLoadingChange?.(true);

        // Attempt to save site details and verify access
        getCMResponse('/datasource/google-search-console/save-site-details', 'POST', {
            project_id: project.id,
            site_url: siteUrl
        })
        .then((response) => {
            // console.log('Google Search Console site verification successful:', response);
            setIsVerifying(false);
            setShowUrlDialog(false);
            onGrantedScopesUpdate();
        })
        .catch((error) => {
            // console.error('Error verifying Google Search Console site:', error);
            setError(error.message || 'Failed to verify site access');
            setIsVerifying(false);
        })
        .finally(() => {
            onLoadingChange?.(false);
        });
    }, [project, onGrantedScopesUpdate, onLoadingChange]);

    const handleDialogCancel = useCallback(() => {
        setShowUrlDialog(false);
        setError(null);
        setIsVerifying(false);
    }, []);

    // If completed, show the icon but make it unclickable. Bug fix:
    // datasource-reconnect-menu-noop/trd.md, Requirement 2 (Gap B) --
    // GoogleDataAccess must stay mounted even once connected (it already
    // renders null of its own accord in managed mode, so this causes no
    // visual change) so its unconditional onLoginReady effect fires and
    // loginRefs.current['GOOGLESEARCHCONSOLE'] gets populated, matching
    // MetaAuth/GoogleMerchantCenterAuth's pattern.
    if (isCompleted) {
        return (
            <div className={styles.container}>
                <img
                    src={GoogleSearchConsoleIcon}
                    alt="Google Search Console - Connected"
                    className={`${styles.iconButton} ${styles.completed}`}
                />
                <GoogleDataAccess
                    requiredScopes={requiredScopes}
                    onAccessGrant={handleAuthCallback}
                    icon={GoogleSearchConsoleIcon}
                    project={project}
                    provider="GOOGLESEARCHCONSOLE"
                    onLoginReady={onLoginReady}
                />
            </div>
        );
    }

    // If verifying, show loading state
    if (isVerifying) {
        return (
            <div className={styles.container}>
                <div className={styles.verifying}>
                    <img 
                        src={GoogleSearchConsoleIcon} 
                        alt="Google Search Console - Verifying" 
                        className={`${styles.iconButton} ${styles.loading}`}
                    />
                    <p className={styles.verifyingText}>Verifying site access...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {!showUrlDialog ? (
                <GoogleDataAccess
                    requiredScopes={requiredScopes}
                    onAccessGrant={handleAuthCallback}
                    icon={GoogleSearchConsoleIcon}
                    project={project}
                    provider="GOOGLESEARCHCONSOLE"
                    onLoginReady={onLoginReady}
                />
            ) : (
                <SiteUrlInputDialog
                    onUrlSubmit={handleUrlSubmit}
                    onCancel={handleDialogCancel}
                    isVerifying={isVerifying}
                    error={error}
                />
            )}
        </div>
    );
};

export default GoogleSearchConsoleAuth;